import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Button from '@/components/ui/Button'
import NumberStepper from '@/components/ui/NumberStepper'
import ScheduleSeriesWizard from '@/components/schedule/ScheduleSeriesWizard'
import {
  getScheduleTemplates,
  createScheduleTemplate,
  deleteScheduleTemplate,
  updateScheduleTemplate,
  ScheduleTemplate,
} from '@/services/schedule'
import api from '@/services/api'

interface TrainerSettings {
  default_session_duration: number
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Понедельник', short: 'Пн' },
  { value: 1, label: 'Вторник', short: 'Вт' },
  { value: 2, label: 'Среда', short: 'Ср' },
  { value: 3, label: 'Четверг', short: 'Чт' },
  { value: 4, label: 'Пятница', short: 'Пт' },
  { value: 5, label: 'Суббота', short: 'Сб' },
  { value: 6, label: 'Воскресенье', short: 'Вс' },
]

export default function ScheduleSettingsPage() {
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([])
  const [settings, setSettings] = useState<TrainerSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState(() => (new Date().getDay() + 6) % 7)

  // Расчёт end_time на основе start_time и длительности из настроек
  const calcEndTime = (startTime: string, duration?: number) => {
    const d = duration ?? settings?.default_session_duration ?? 60
    const [hours, mins] = startTime.split(':').map(Number)
    const totalMins = hours * 60 + mins + d
    const endHours = Math.min(Math.floor(totalMins / 60), 23)
    const endMins = totalMins % 60
    return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
  }

  // Форма для нового шаблона
  const [showAddForm, setShowAddForm] = useState(false)
  const [newTemplate, setNewTemplate] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '10:00',
    max_clients: 1,
  })
  const [isAdding, setIsAdding] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [notice, setNotice] = useState('')

  // Загрузка данных
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [templatesData, profileData] = await Promise.all([
        getScheduleTemplates(),
        api.get<TrainerSettings>('/trainers/me'),
      ])
      setTemplates(templatesData)
      setSettings(profileData.data)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Добавление шаблона
  const handleAddTemplate = async () => {
    setIsAdding(true)
    try {
      await createScheduleTemplate(newTemplate)
      setShowAddForm(false)
      setSelectedDay(newTemplate.day_of_week)
      setNewTemplate({
        day_of_week: 0,
        start_time: '09:00',
        end_time: calcEndTime('09:00'),
        max_clients: 1,
      })
      loadData()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка добавления интервала')
    } finally {
      setIsAdding(false)
    }
  }

  // Удаление шаблона
  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Удалить этот интервал из расписания недели?')) return
    try {
      await deleteScheduleTemplate(id)
      loadData()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка удаления')
    }
  }

  // Переключение активности шаблона
  const handleToggleTemplate = async (template: ScheduleTemplate) => {
    try {
      await updateScheduleTemplate(template.id, { is_active: !template.is_active })
      loadData()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка обновления')
    }
  }

  const day = DAYS_OF_WEEK[selectedDay]
  const dayTemplates = templates
    .filter((t) => t.day_of_week === selectedDay)
    .sort((a, b) => a.start_time.localeCompare(b.start_time))

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-tg-hint">Загрузка...</div>
      </div>
    )
  }

  if (showWizard) {
    return (
      <div className="p-4">
        <ScheduleSeriesWizard
          defaultDuration={settings?.default_session_duration ?? 60}
          defaultDay={selectedDay}
          onCancel={() => setShowWizard(false)}
          onCreated={(created, skipped, replaced, days) => {
            setSelectedDay(days[0] ?? selectedDay)
            setShowWizard(false)
            setNotice(
              `Добавлено: ${created}. Заменено: ${replaced}. Оставлено без изменений: ${skipped}. Теперь можно сгенерировать слоты на нужные даты в расписании.`
            )
            void loadData()
          }}
        />
      </div>
    )
  }

  return (
    <div className="p-4">
      {/* Заголовок */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/schedule" className="text-tg-hint">
          ← Назад
        </Link>
        <h1 className="text-xl font-bold text-tg-text">Расписание недели</h1>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-5" role="group" aria-label="Дни недели">
        {DAYS_OF_WEEK.map((day) => {
          const count = templates.filter((t) => t.day_of_week === day.value).length
          return (
            <button
              key={day.value}
              type="button"
              aria-label={`${day.label}, интервалов: ${count}`}
              aria-pressed={selectedDay === day.value}
              onClick={() => {
                setSelectedDay(day.value)
                setShowAddForm(false)
              }}
              className={`min-h-14 py-2 rounded-xl ${selectedDay === day.value ? 'bg-tg-button text-tg-button-text' : 'bg-tg-secondary text-tg-text'}`}
            >
              <span className="block text-sm font-medium">{day.short}</span>
              <span className="block text-xs mt-1 opacity-75">{count}</span>
            </button>
          )
        })}
      </div>
      <div>
        {notice && (
          <p role="status" className="text-sm text-tg-text bg-tg-secondary rounded-xl p-4 mb-4">
            {notice}
          </p>
        )}
        {!showAddForm && (
          <Button
            onClick={() => {
              setNotice('')
              setShowWizard(true)
            }}
            fullWidth
            className="mb-2"
          >
            Создать расписание
          </Button>
        )}
        {/* Кнопка добавления */}
        {!showAddForm && (
          <Button
            onClick={() => {
              const startTime = '09:00'
              setNewTemplate({
                day_of_week: selectedDay,
                start_time: startTime,
                end_time: calcEndTime(startTime),
                max_clients: 1,
              })
              setShowAddForm(true)
            }}
            fullWidth
            variant="outline"
            className="mb-4"
          >
            + Добавить интервал
          </Button>
        )}

        {/* Форма добавления */}
        {showAddForm && (
          <div className="bg-tg-secondary rounded-xl p-4 mb-4">
            <h3 className="font-medium text-tg-text mb-3">Дополнительный интервал</h3>

            {/* День недели */}
            <div className="mb-3">
              <label className="block text-sm text-tg-hint mb-1">День недели</label>
              <select
                value={newTemplate.day_of_week}
                onChange={(e) =>
                  setNewTemplate({ ...newTemplate, day_of_week: Number(e.target.value) })
                }
                className="w-full p-2 rounded-lg border border-gray-300 bg-white text-black"
              >
                {DAYS_OF_WEEK.map((day) => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Время */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm text-tg-hint mb-1">Начало</label>
                <input
                  type="time"
                  value={newTemplate.start_time}
                  onChange={(e) => {
                    const startTime = e.target.value
                    setNewTemplate({
                      ...newTemplate,
                      start_time: startTime,
                      end_time: calcEndTime(startTime),
                    })
                  }}
                  className="w-full p-2 rounded-lg border border-gray-300 bg-white text-black"
                />
              </div>
              <div>
                <label className="block text-sm text-tg-hint mb-1">Конец</label>
                <input
                  type="time"
                  value={newTemplate.end_time}
                  onChange={(e) => setNewTemplate({ ...newTemplate, end_time: e.target.value })}
                  className="w-full p-2 rounded-lg border border-gray-300 bg-white text-black"
                />
              </div>
            </div>

            {/* Вместимость */}
            <div className="mb-4">
              <label className="block text-sm text-tg-hint mb-1">Макс. клиентов</label>
              <NumberStepper
                value={newTemplate.max_clients}
                onChange={(max_clients) => setNewTemplate({ ...newTemplate, max_clients })}
              />
            </div>

            {/* Кнопки */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                Отмена
              </Button>
              <Button onClick={handleAddTemplate} loading={isAdding} className="flex-1">
                Добавить
              </Button>
            </div>
          </div>
        )}

        <section aria-label={`Интервалы: ${day.label}`}>
          <h2 className="font-medium text-tg-text mb-3">{day.label}</h2>
          {dayTemplates.length === 0 ? (
            <p className="text-sm text-tg-hint py-4">
              На этот день интервалов пока нет. Создайте расписание или добавьте отдельный интервал.
            </p>
          ) : (
            <div className="space-y-2">
              {dayTemplates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between gap-2 p-3 rounded-lg bg-tg-secondary text-tg-text"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      type="button"
                      aria-label={`${template.is_active ? 'Отключить' : 'Включить'} интервал ${template.start_time}–${template.end_time}`}
                      aria-pressed={template.is_active}
                      onClick={() => handleToggleTemplate(template)}
                      className={`w-11 h-11 shrink-0 rounded-full border flex items-center justify-center ${template.is_active ? 'bg-tg-button border-tg-button text-tg-button-text' : 'border-tg-hint text-tg-hint'}`}
                    >
                      {template.is_active ? '✓' : '−'}
                    </button>
                    <div className={template.is_active ? '' : 'text-tg-hint'}>
                      <span>
                        {template.start_time}–{template.end_time}
                      </span>
                      <p className="text-sm text-tg-hint">
                        {template.is_active ? `До ${template.max_clients} чел.` : 'Отключён'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-red-500 text-sm min-h-11 px-1"
                  >
                    Удалить
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Подсказка */}
        <div className="mt-6 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Интервалы повторяются каждую неделю. По ним создаются слоты на конкретные даты.
            Отключённые интервалы не участвуют в генерации.
          </p>
        </div>
      </div>
    </div>
  )
}
